import { Language, type LanguageValue } from './account.js';

export const LANGUAGE_STORAGE_KEY = 'monomo-language';
export const LOCALE_SUGGEST_CACHE_KEY = 'monomo-locale-suggest';

const CZECH_TIMEZONES = new Set(['Europe/Prague', 'Europe/Bratislava']);

export function normalizeLanguage(value: string | null | undefined): LanguageValue | null {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (normalized.startsWith('cs') || normalized.startsWith('sk')) {
    return Language.CS;
  }
  if (normalized.startsWith('en')) {
    return Language.EN;
  }

  return null;
}

export function parseBrowserLanguage(
  languages: readonly string[] | null | undefined,
  fallbackLanguage?: string | null,
): LanguageValue | null {
  for (const language of languages ?? []) {
    const parsed = normalizeLanguage(language);
    if (parsed) {
      return parsed;
    }
  }

  return normalizeLanguage(fallbackLanguage);
}

export function parseAcceptLanguageHeader(header: string | null | undefined): LanguageValue | null {
  if (!header) {
    return null;
  }

  const candidates = header
    .split(',')
    .map((part) => part.split(';')[0]?.trim())
    .filter(Boolean);

  return parseBrowserLanguage(candidates);
}

export function languageFromCountry(country: string | null | undefined): LanguageValue | null {
  if (!country) {
    return null;
  }

  const normalized = country.trim().toUpperCase();
  if (normalized === 'CZ' || normalized === 'SK') {
    return Language.CS;
  }

  return Language.EN;
}

export function languageFromTimeZone(timeZone: string | null | undefined): LanguageValue | null {
  if (!timeZone) {
    return null;
  }

  return CZECH_TIMEZONES.has(timeZone) ? Language.CS : null;
}

export function resolveLanguageFromLocation(input: {
  country?: string | null;
  timeZone?: string | null;
}): LanguageValue | null {
  return languageFromCountry(input.country) ?? languageFromTimeZone(input.timeZone);
}

export function resolveInitialLanguage(input: {
  storedLanguage?: string | null;
  browserLanguages?: readonly string[];
  browserLanguage?: string | null;
  country?: string | null;
  timeZone?: string | null;
  fallback?: LanguageValue;
}): LanguageValue {
  return (
    normalizeLanguage(input.storedLanguage)
    ?? parseBrowserLanguage(input.browserLanguages, input.browserLanguage)
    ?? resolveLanguageFromLocation(input)
    ?? input.fallback
    ?? Language.CS
  );
}
